import { Badge, Box, Divider, Flex, Text, VStack, Wrap, WrapItem } from '@chakra-ui/react'
import {
  AboBloodType,
  AntibodyScreenResult,
  FieldMapValue,
  GbsStatus,
  LabOrderedEntry,
  LabsData,
  NstBppEntry,
  NstResult,
  NstTestType,
  OtherLabEntry,
  RhFactor,
  RhogamAdministration,
} from '@hb/shared'
import { SectionLabel } from '../../SectionLabel'

const LabelText = ({ label }: { label: string }) => (
  <Text fontSize="xs" fontWeight={600} color="gray.500" _dark={{ color: 'gray.400' }} mb={0.5}>
    {label}
  </Text>
)

const gbsColorScheme: Record<GbsStatus, string> = {
  negative: 'green',
  positive: 'red',
  unknown: 'gray',
}

const gbsLabel: Record<GbsStatus, string> = {
  negative: 'Negative',
  positive: 'Positive',
  unknown: 'Unknown / Pending',
}

const flagColorScheme: Record<string, string> = {
  normal: 'green',
  high: 'red',
  low: 'orange',
  abnormal: 'purple',
}

const antibodyResultLabel: Record<AntibodyScreenResult, string> = {
  negative: 'Negative',
  positive: 'Positive',
  'weakly-positive': 'Weakly Positive',
}

const LabsOrderedList = ({ labs }: { labs: LabOrderedEntry[] }) => (
  <Box>
    <LabelText label="Labs Ordered This Visit" />
    <VStack align="stretch" spacing={1}>
      {labs.map((lab, i) => (
        <Flex key={i} gap={2} fontSize="sm" align="baseline">
          <Text fontWeight={600} color="gray.700" _dark={{ color: 'gray.200' }}>
            {lab.labName}
          </Text>
          {lab.clinicalIndication && <Text color="gray.500">— {lab.clinicalIndication}</Text>}
        </Flex>
      ))}
    </VStack>
  </Box>
)

const OtherLabsList = ({ labs }: { labs: OtherLabEntry[] }) => (
  <Box>
    <LabelText label="Other Labs" />
    <VStack align="stretch" spacing={1}>
      {labs.map((lab, i) => (
        <Flex key={i} gap={2} fontSize="sm" align="center">
          <Text fontWeight={600} color="gray.700" _dark={{ color: 'gray.200' }}>
            {lab.labName}
          </Text>
          {lab.result && <Text color="gray.600">— {lab.result}</Text>}
          {lab.flagStatus && lab.flagStatus !== 'normal' && (
            <Badge colorScheme={flagColorScheme[lab.flagStatus] ?? 'gray'} fontSize="xs">
              {lab.flagStatus.toUpperCase()}
            </Badge>
          )}
        </Flex>
      ))}
    </VStack>
  </Box>
)

const aboLabel: Record<AboBloodType, string> = { O: 'O', A: 'A', B: 'B', AB: 'AB' }

const BloodTypeSection = ({
  bloodTypeAbo,
  bloodTypeRh,
  antibodyScreenOrdered,
  antibodyScreenResult,
  rhogamAdministration,
  rhogamPostDeliveryIndicated,
}: {
  bloodTypeAbo?: AboBloodType
  bloodTypeRh?: RhFactor
  antibodyScreenOrdered?: boolean
  antibodyScreenResult?: AntibodyScreenResult
  rhogamAdministration?: RhogamAdministration
  rhogamPostDeliveryIndicated?: boolean
}) => (
  <Box>
    <LabelText label="Blood Type & Rh Status" />
    <Flex gap={2} align="center" flexWrap="wrap">
      {bloodTypeAbo && (
        <Badge colorScheme="blue" fontSize="sm" px={2} py={0.5}>
          {aboLabel[bloodTypeAbo]}
        </Badge>
      )}
      {bloodTypeRh && (
        <Badge
          colorScheme={bloodTypeRh === 'negative' ? 'orange' : 'green'}
          fontSize="sm"
          px={2}
          py={0.5}>
          Rh {bloodTypeRh === 'negative' ? 'Negative (−)' : 'Positive (+)'}
        </Badge>
      )}
      {antibodyScreenOrdered && !antibodyScreenResult && (
        <Badge colorScheme="blue" fontSize="xs">
          Antibody Screen Ordered
        </Badge>
      )}
      {antibodyScreenResult && (
        <Badge
          colorScheme={antibodyScreenResult === 'negative' ? 'green' : 'red'}
          fontSize="xs"
          px={2}
          py={0.5}>
          Antibody Screen: {antibodyResultLabel[antibodyScreenResult]}
        </Badge>
      )}
    </Flex>
    {bloodTypeRh === 'negative' && antibodyScreenResult && antibodyScreenResult !== 'negative' && (
      <Text fontSize="xs" color="red.600" mt={1} fontWeight={500}>
        Sensitized — MFM consult recommended
      </Text>
    )}
    {rhogamAdministration?.date && (
      <Text fontSize="sm" mt={2}>
        RhoGAM: {rhogamAdministration.dose ?? '—'} on {rhogamAdministration.date}
        {rhogamAdministration.lotNumber ? ` (Lot: ${rhogamAdministration.lotNumber})` : ''}
        {rhogamAdministration.route ? ` via ${rhogamAdministration.route}` : ''}
        {rhogamAdministration.consentObtained ? ' · Consent obtained' : ''}
      </Text>
    )}
    {rhogamPostDeliveryIndicated && (
      <Badge colorScheme="orange" fontSize="xs" mt={1}>
        RhoGAM Indicated Post-Delivery
      </Badge>
    )}
  </Box>
)

const nstTestTypeLabel: Record<NstTestType, string> = {
  nst: 'NST',
  'modified-bpp': 'Modified BPP',
  'full-bpp': 'Full BPP',
}

const nstResultColorScheme: Record<NstResult, string> = {
  reactive: 'green',
  'non-reactive': 'red',
  equivocal: 'orange',
}

const nstResultLabel: Record<NstResult, string> = {
  reactive: 'Reactive',
  'non-reactive': 'Non-Reactive',
  equivocal: 'Equivocal',
}

const bppScoreColorScheme = (score: number) => {
  if (score >= 8) return 'green'
  if (score === 6) return 'orange'
  return 'red'
}

const NstBppSection = ({ nstBpp }: { nstBpp: NstBppEntry }) => (
  <Box>
    <LabelText label="NST / BPP" />
    <Flex gap={2} align="center" flexWrap="wrap">
      {nstBpp.testType && (
        <Badge colorScheme="blue" fontSize="xs" px={2} py={0.5}>
          {nstTestTypeLabel[nstBpp.testType]}
        </Badge>
      )}
      {nstBpp.testDate && (
        <Text fontSize="sm" color="gray.500">
          {nstBpp.testDate}
        </Text>
      )}
      {nstBpp.result && (
        <Badge colorScheme={nstResultColorScheme[nstBpp.result]} fontSize="xs" px={2} py={0.5}>
          {nstResultLabel[nstBpp.result]}
        </Badge>
      )}
      {nstBpp.bppScore != null && (
        <Badge colorScheme={bppScoreColorScheme(nstBpp.bppScore)} fontSize="xs" px={2} py={0.5}>
          BPP {nstBpp.bppScore}/10
        </Badge>
      )}
      {nstBpp.performedBy && (
        <Text fontSize="xs" color="gray.500">
          by {nstBpp.performedBy}
        </Text>
      )}
    </Flex>
    {nstBpp.notes && (
      <Text
        fontSize="sm"
        color="gray.600"
        _dark={{ color: 'gray.300' }}
        mt={1}
        whiteSpace="pre-wrap">
        {nstBpp.notes}
      </Text>
    )}
  </Box>
)

export const LabsView = ({ data }: { data: FieldMapValue }) => {
  const labs = data.labs as LabsData | undefined

  if (!labs) return null

  const {
    labsOrderedThisVisit,
    labReviewConfirmation,
    gbsStatus,
    gbsDate,
    gctValueMgdl,
    gttOrdered,
    gttResult,
    gdmDiagnosed,
    otherLabs,
    bloodTypeAbo,
    bloodTypeRh,
    antibodyScreenOrdered,
    antibodyScreenResult,
    rhogamAdministration,
    rhogamPostDeliveryIndicated,
    nstBpp,
  } = labs

  const hasAnyData =
    labsOrderedThisVisit?.length ||
    labReviewConfirmation ||
    gbsStatus ||
    gctValueMgdl != null ||
    otherLabs?.length ||
    bloodTypeAbo ||
    bloodTypeRh ||
    nstBpp?.testType

  if (!hasAnyData) return null

  return (
    <VStack align="flex-start" spacing={3} w="100%">
      <SectionLabel>LABS &amp; DIAGNOSTICS</SectionLabel>

      {labsOrderedThisVisit && labsOrderedThisVisit.length > 0 && (
        <LabsOrderedList labs={labsOrderedThisVisit} />
      )}

      {labReviewConfirmation && (
        <Box>
          <LabelText label="Lab Results Review" />
          <Badge colorScheme="green" fontSize="xs">
            Reviewed and acknowledged
          </Badge>
        </Box>
      )}

      {(bloodTypeAbo || bloodTypeRh) && (
        <>
          <Divider />
          <BloodTypeSection
            bloodTypeAbo={bloodTypeAbo}
            bloodTypeRh={bloodTypeRh}
            antibodyScreenOrdered={antibodyScreenOrdered}
            antibodyScreenResult={antibodyScreenResult}
            rhogamAdministration={rhogamAdministration}
            rhogamPostDeliveryIndicated={rhogamPostDeliveryIndicated}
          />
        </>
      )}

      {gbsStatus && (
        <>
          <Divider />
          <Box>
            <LabelText label="GBS Status" />
            <Wrap spacing={2} align="center">
              <WrapItem>
                <Badge colorScheme={gbsColorScheme[gbsStatus]} fontSize="sm" px={2} py={0.5}>
                  {gbsLabel[gbsStatus]}
                </Badge>
              </WrapItem>
              {gbsDate && (
                <WrapItem>
                  <Text fontSize="sm" color="gray.500">
                    Collected: {gbsDate}
                  </Text>
                </WrapItem>
              )}
            </Wrap>
            {gbsStatus === 'positive' && (
              <Text fontSize="xs" color="red.600" mt={1} fontWeight={500}>
                Intrapartum antibiotic prophylaxis indicated
              </Text>
            )}
          </Box>
        </>
      )}

      {gctValueMgdl != null && (
        <>
          <Divider />
          <Box>
            <LabelText label="Glucose Screen (GCT)" />
            <Flex gap={3} align="center" flexWrap="wrap">
              <Text fontSize="sm" fontWeight={600}>
                {gctValueMgdl} mg/dL
              </Text>
              {gctValueMgdl >= 130 && (
                <Badge colorScheme="orange" fontSize="xs">
                  ≥ 130 — GTT indicated
                </Badge>
              )}
              {gttOrdered && (
                <Badge colorScheme="blue" fontSize="xs">
                  3-hr GTT ordered
                </Badge>
              )}
              {gttResult && (
                <Badge colorScheme={gttResult === 'pass' ? 'green' : 'red'} fontSize="xs">
                  GTT: {gttResult.toUpperCase()}
                </Badge>
              )}
              {gdmDiagnosed && (
                <Badge colorScheme="red" fontSize="xs" px={2} py={0.5}>
                  GDM Diagnosed
                </Badge>
              )}
            </Flex>
          </Box>
        </>
      )}

      {nstBpp?.testType && (
        <>
          <Divider />
          <NstBppSection nstBpp={nstBpp} />
        </>
      )}

      {otherLabs && otherLabs.length > 0 && (
        <>
          <Divider />
          <OtherLabsList labs={otherLabs} />
        </>
      )}
    </VStack>
  )
}
